import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Only a plain left click follows a link in place; anything else opens it elsewhere. */
const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/** The in-app path a click would navigate to, or null when it leaves the app or stays put. */
const findInAppTarget = (event: MouseEvent, currentPath: string): string | null => {
  if (event.defaultPrevented || !isPlainLeftClick(event)) return null;
  const anchor =
    event.target instanceof Element ? event.target.closest("a[href]") : null;
  if (!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank") {
    return null;
  }
  const url = new URL(anchor.href);
  if (url.origin !== window.location.origin) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  return path === currentPath ? null : path;
};

/**
 * Holds the user on a page with unsaved edits. The app runs on a BrowserRouter, which has no
 * navigation blocker, so two leaks are closed by hand: a reload or a closed tab gets the
 * browser's own prompt, and a click on any in-app link (sidebar, breadcrumb, a row's link) is
 * caught before React Router sees it and parked until the user decides. The browser's back
 * button is not caught.
 */
export function useLeavePageGuard(isDirty: boolean) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    // Registered on the document in the capture phase, so it runs before React's root
    // listener and the link's own navigation never starts.
    const handleClick = (event: MouseEvent) => {
      const path = findInAppTarget(event, currentPath);
      if (!path) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingPath(path);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
    };
  }, [isDirty, currentPath]);

  const stay = () => setPendingPath(null);

  const leave = () => {
    if (pendingPath) navigate(pendingPath);
    setPendingPath(null);
  };

  return { isAskingToLeave: pendingPath !== null, stay, leave };
}
